// @ts-ignore - jwt-decode v2 has no type declarations
import jwt_decode from "jwt-decode";
import sypiAdapter from "@features/sypi/adapters";
import selectClientsAdapter from "@features/select-clients/adapters";
import generalEndPointsAdapter from "@adapters/general-endpoints.js";
import { setLocalAndSessionOnLogin } from "@utils/session-maintenance-helpers.js";
import { dtpInit } from "@features/dtp/dtpInterface.js";
import { getNowISO, roundToInterval } from "@utils/time-helpers";
import { getBrowserParams, getSamlLoginRoute } from "@utils/browser-helpers";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  closeDock,
  resetFilterButtonClick,
  displayBlockerContainer,
  setCTIDOptions,
  setCommercialCTIDOptions,
  setUserRole,
  reportError,
  enableSave,
  setStatus,
  clearFormFields,
  setIsCommercialClient,
} from "@actions/global";
import {
  loadMaintenanceModeDetails,
  loadMaintenanceModeClients,
  setShowMaintenanceMode,
  loadMaintenanceModeDefaultMessaging,
  resetMaintenanceModeAfterCancel,
} from "@features/sypi/actions";
import { timeEnrichment } from "@reducers/_utils/reducer-helpers.js";
import { getStore } from "@store";
import { userErrorMessage } from "@utils/custom-errors";
import {
  getDevUserRole,
  logDevOverride,
} from "@/shared/dev/dev-role-override";
import type { Store } from "redux";
import type {
  SelectClientsRootState,
  SelectClientsClient,
  MaintenanceModeClient,
  ClientCTIDsResponse,
} from "./types";

interface JWTPayload {
  ctid?: number;
  [key: string]: any;
}

interface ErrorPayload {
  message: string;
  logout?: boolean;
  redirect?: string;
  callback?: () => void;
}

export const selectClientsInit = async (store: Store = getStore()): Promise<void> => {
  store.dispatch(showSpinner());
  // NOTE: getConfigurationDetails is intentionally NOT called here.
  // At this stage the user has not yet selected a client, so localStorage
  // has no "ctid". Calling console_config without a ctid produces a
  // malformed API URL (/api/2500//console_config) which the backend
  // rejects — ultimately redirecting the user to /logout?error=NO_CRED.
  // getConfigurationDetails is called later, after the ctid is set in
  // confirmClientSelection().
  loadClientsDisplay(store);
  getMaintenanceModeDetails(store);
  dtpInit(store);
  store.dispatch(stopSpinner());
};

export const getMaintenanceModeDetails = async (store: Store = getStore()): Promise<void> => {
  try {
    let maintenanceModeProdClientList: MaintenanceModeClient[] =
      await generalEndPointsAdapter.getMaintenanceModeClientList({});
    maintenanceModeProdClientList.map((client: MaintenanceModeClient) => {
      if (client.role === "staging") client.role = "UAT";
    });
    store.dispatch(loadMaintenanceModeClients(maintenanceModeProdClientList));
    const baseCtid: number = window.location.hostname.includes("dev") ? 261 : 527;
    const baseClientSchema = await sypiAdapter.fetchSypiSchema(
      { release: "v4.10" },
      String(baseCtid)
    );
    const { master_schema } = baseClientSchema;
    const { properties } = master_schema;
    const { content } = properties;
    store.dispatch(
      loadMaintenanceModeDefaultMessaging(content?.properties?.maintenance)
    );
    let maintenanceModeDetails =
      await generalEndPointsAdapter.getMaintenanceModeStatus();
    maintenanceModeDetails = {
      ...maintenanceModeDetails,
      ...timeEnrichment(maintenanceModeDetails, "view"),
    };
    store.dispatch(loadMaintenanceModeDetails(maintenanceModeDetails));
    store.dispatch(enableSave());
  } catch (err) {
    console.warn(err);
  }
};

export const handleInitialAuth = async (store: Store = getStore()): Promise<void> => {
  store.dispatch(showSpinner());
  setLocalAndSessionOnLogin();
  store.dispatch(setToastMessage("You are being redirected. Please wait..."));
  const URL_PARAMS = getBrowserParams(window.location.search) as Record<string, string>;
  if (URL_PARAMS.jwt && URL_PARAMS.jwt.length) {
    localStorage.setItem("jwt", URL_PARAMS.jwt);
    window.location.replace("/select-clients");
  } else {
    generalEndPointsAdapter.logout();
    const searchParams = getSamlLoginRoute();
    window.location.replace(searchParams);
  }
  store.dispatch(stopSpinner());
};

export const saveMaintenanceMode = async (
  idTarget?: string,
  updateStatus: string = "",
  store: Store = getStore()
): Promise<void> => {
  store.dispatch(showSpinner());
  store.dispatch(closeDock());
  const { maintenanceMode, config } = store.getState() as SelectClientsRootState;
  const { status } = maintenanceMode;
  const scheduledDate: boolean =
    maintenanceMode.inputISOStopDate === null &&
    maintenanceMode.inputISOStartDate === null;
  let inactive: boolean = scheduledDate && status === "Inactive";

  if (status === "Active" && updateStatus === "Canceled") {
    await generalEndPointsAdapter.activateMaintenanceModeSession({
      status: status,
      id: maintenanceMode.id,
    });
    store.dispatch(setToastMessage(`Maintenance Details have been updated`));
    store.dispatch(stopSpinner());
  } else {
    const mutableMaintenanceMode = { ...maintenanceMode } as Record<string, any>;
    if ((scheduledDate && status !== "Inactive") || inactive) {
      mutableMaintenanceMode.inputISOStartDate = roundToInterval(
        getNowISO(config.timeZone),
        1
      );
      inactive = true;
    }
    let maintenanceModePayload: Record<string, any> = {
      ...mutableMaintenanceMode,
      ...timeEnrichment(mutableMaintenanceMode, "send"),
    };
    maintenanceModePayload = (({
      ctids,
      id: _id,
      status: _status,
      inputISOStartDate,
      inputISOStopDate,
      original_start_date,
      original_stop_date,
      errors,
      validInputs,
      saveEnabled,
      showMaintenanceMode,
      initialMaintenanceMode,
      ...otherKeys
    }: Record<string, any>) => ({ status: _status, ...otherKeys }))({
      ...(maintenanceModePayload ? maintenanceModePayload : {}),
      ...(updateStatus ? { status: updateStatus } : {}),
    });

    let result = await generalEndPointsAdapter.createMaintenanceModeSession(
      maintenanceModePayload
    );
    if (typeof result !== "string") {
      store.dispatch(stopSpinner());
      result = { ...result, ...timeEnrichment(result, "view") };
      store.dispatch(loadMaintenanceModeDetails(result));
      store.dispatch(
        setStatus(
          (scheduledDate && status !== "Inactive") || inactive
            ? "Active"
            : result.status
        )
      );
      store.dispatch(setToastMessage(`Maintenance Details have been updated`));
    } else {
      store.dispatch(
        reportError(userErrorMessage("update", "Maintenance Details", result))
      );
      store.dispatch(clearFormFields());
      store.dispatch(stopSpinner());
    }
  }
  if (inactive) {
    // Re-read the store: the PUT response above just dispatched
    // loadMaintenanceModeDetails(result), so the freshly-created window's
    // id now lives in state. The local `maintenanceMode` const captured
    // at the top of this function is stale and would still be undefined
    // for a brand-new window.
    const { maintenanceMode: latestMaintenanceMode } =
      store.getState() as SelectClientsRootState;
    await generalEndPointsAdapter.activateMaintenanceModeSession({
      status: "Scheduled",
      id: latestMaintenanceMode.id,
    });
  }
  if (updateStatus === "Canceled") {
    // Fully clear the cancelled window from local state.
    //
    // Previously this dispatched loadMaintenanceModeDetails({...maintenanceMode, ...})
    // which spread the STALE pre-cancel state (start_date, stop_date, id,
    // status, initialMaintenanceMode) back into the store — the reducer's
    // trailing `...action.maintenanceModeDetails` spread overrode the
    // explicit nulls with those stale values. Result: after cancel the
    // dates stayed prefilled, the "Activate Maintenance Mode" toggle
    // remained on (because status was still "Active"/"Scheduled") and the
    // "Cancel Maintenance" button was still shown, letting the user cancel
    // the same window again.
    //
    // The dedicated reset action clears every window-scoped field while
    // preserving the loaded client list (ctids) and messaging defaults.
    store.dispatch(resetMaintenanceModeAfterCancel());
  }
  store.dispatch(enableSave());
  store.dispatch(setShowMaintenanceMode(false));
};

export const loadClientsDisplay = async (store: Store = getStore()): Promise<void> => {
  store.dispatch(showSpinner());
  let errorLog: ErrorPayload | null = null;
  const availableClients: ClientCTIDsResponse = await selectClientsAdapter.fetchAllClientCTIDs();
  const commercialClients: ClientCTIDsResponse = await selectClientsAdapter.fetchCommercialClientCTIDs();
  if (availableClients && availableClients.errors.length === 0) {
    errorLog =
      availableClients.clients.length <= 0
        ? {
          message: "You do not have access to any client",
        }
        : null;
  } else {
    errorLog = {
      message: "You do not have access to any client",
    };
  }
  if (errorLog) {
    store.dispatch(
      setToastMessage(
        errorLog.message,
        errorLog.callback ? errorLog.callback : undefined
      )
    );
  }
  store.dispatch(setCTIDOptions(availableClients as any));
  store.dispatch(setCommercialCTIDOptions(commercialClients as any));
  // Local-dev-only: allow overriding user_role via VITE_DEV_USER_ROLE or
  // window.__DEV_USER_ROLE__. In prod builds this branch is DCE'd.
  const devUserRole = getDevUserRole();
  const effectiveUserRole = devUserRole ?? availableClients.user_role;
  if (devUserRole) {
    logDevOverride("userRole", availableClients.user_role, devUserRole);
  }
  store.dispatch(setUserRole(effectiveUserRole));
  !errorLog ? store.dispatch(displayBlockerContainer(false)) : null;
  store.dispatch(stopSpinner());
};

export const confirmClientSelection = async (
  client: SelectClientsClient,
  action: string,
  store: Store = getStore()
): Promise<void> => {
  store.dispatch(displayBlockerContainer(true));
  store.dispatch(showSpinner());
  const { commercialClients } = (store.getState() as SelectClientsRootState).config;
  const isCommercialClient: boolean = commercialClients
    .map((commercialClient: SelectClientsClient) => commercialClient.clienttype_id)
    .includes(client.clienttype_id);
  let errorPayload: ErrorPayload = { message: "" };
  if (action === "cancel") {
    store.dispatch(resetFilterButtonClick());
    store.dispatch(
      setToastMessage(`Action canceled. You are beeing redirected.`)
    );
  } else {
    const payload = {
      clienttype_id: client.clienttype_id,
    };
    const confirmedClient = isCommercialClient
      ? await selectClientsAdapter.confirmCommercialClient(payload)
      : await selectClientsAdapter.confirmClient(payload);
    const session =
      confirmedClient && confirmedClient?.errors?.length === 0
        ? await selectClientsAdapter.confirmNewSession({
          clienttype_id: client.clienttype_id,
        })
        : null;

    const isOnSelectClients = window.location.pathname === "/select-clients";
    errorPayload =
      (confirmedClient && confirmedClient?.errors?.length > 0) ||
        (session && session?.errors?.length > 0)
        ? {
          message: "This action was unsuccessful. Please, login again",
          logout: true,
          redirect: isOnSelectClients ? undefined : "/select-clients",
        }
        : {
          message: `Client confirmed. You can now access client ${client.name}`,
          logout: false,
          redirect: "/",
        };

    let jwt: string[] | null = !errorPayload.logout
      ? confirmedClient.Authorization.split(" ")
      : null;
    const hasSyfJwt: boolean = !!(jwt && jwt[0] === "SYF");
    if (hasSyfJwt && jwt) {
      localStorage.setItem("jwt", jwt[1]);
    } else {
      errorPayload = {
        message: "This action was unsuccessful. Please, login again",
        logout: true,
        redirect: isOnSelectClients ? undefined : "/select-clients",
      };
    }
    if (hasSyfJwt && jwt) {
      localStorage.setItem("ctid", String(jwt_decode<JWTPayload>(jwt[1]).ctid));
    }

    if (!errorPayload.logout) {
      setLocalAndSessionOnLogin();
    }
  }
  store.dispatch(
    setToastMessage(errorPayload.message, () => {
      if (errorPayload.redirect) {
        window.location.href = errorPayload.redirect;
      }
      store.dispatch(stopSpinner());
    })
  );
  store.dispatch(closeDock());
};
